/**
 * Dropdown-related field renderers for DeviceModal.
 *
 * Exports `renderField` which dispatches to dropdown/input helpers.
 */
import { FusionInputField } from 'components';
import BaseSelect from 'components/Selects/BaseSelect/BaseSelect';
import { findSelectOption } from 'components/Selects/BaseSelect/utils/baseSelect.utils';
import { Form } from 'react-bootstrap';
import BulkDeviceMultiSelect from '../components/BulkDeviceMultiSelect/BulkDeviceMultiSelect';

// Device types whose form is "pick one row from a list" and so can add many
// rows at once. Keyed by `${system_type}|${device_type}` — the exact name
// strings in `RI_Device_System_Type_Map` / `RI_Device_Type_Map` (use '*' to
// match any device_type). `itemLabel` (singular) controls copy in the toggle
// and the multi-select.
//
// Every type here has the same form shape in `data/DeviceTypeForm.json`: an
// alias plus one `valid-device` picker. Bulk mode blanks the alias (each row
// keeps its own list name) and offers the picker as checkboxes. Types that
// need per-device input — Manual Road Closure's name, node id and map — are
// deliberately absent: one form cannot describe many of them.
export const BULK_SELECT_MATRIX = {
  'USGS|*': { itemLabel: 'station' },
  'RescAlert Devices|Buoy Conditions': { itemLabel: 'buoy' },
  'Flood|Stage': { itemLabel: 'stage device' },
  'RescAlert Devices|Technology Bridge_Siren/Giant Voice': { itemLabel: 'siren' },
  'RescAlert Devices|Technology Bridge_Flashing Lights': { itemLabel: 'flashing light' },
  'Exacq Vision|Overwatch VMS': { itemLabel: 'camera' },
};

export const getBulkSelectConfig = (systemType, deviceType) =>
  BULK_SELECT_MATRIX[`${systemType}|${deviceType}`] ||
  BULK_SELECT_MATRIX[`${systemType}|*`] ||
  null;

/**
 * Render a field definition using component state and helpers.
 * Supports `valid-device` and `input-field` types.
 *
 * @param {object} formData
 * @param {function} setFormData
 * @param {object} field
 * @param {Array<object>} deviceTypes
 * @param {object|null} device
 * @returns {JSX.Element|null}
 */
export const renderField = (formData, setFormData, field, deviceTypes, device) => {
  switch (field.type) {
    case 'valid-device':
      return renderValidDevice(formData, setFormData, field, deviceTypes, device);
    case 'input-field':
      return renderInputField(formData, setFormData, field, device);
    default:
      return null;
  }
};

/**
 * Render a simple input field using `FusionInputField`.
 *
 * @param {object} formData
 * @param {function} setFormData
 * @param {object} field
 * @returns {JSX.Element}
 */
const renderInputField = (formData, setFormData, field, device) => {
  // If this is the alias field and bulk_mode is on, disable and clear it.
  // Only on ADD: an edit form is seeded from the device object, and a device
  // that was bulk-added carries `bulk_mode: true` in its stored properties
  // (RI_Device.data keeps every unknown key the add payload had). Reading
  // that here disabled the alias when editing a single device.
  const isAlias = field['data-field'] === 'alias';
  const bulkMode = !device && formData.bulk_mode === true;
  const value = isAlias && bulkMode ? '' : formData[field['data-field']] || '';

  // If bulk mode is toggled on and alias is not already empty, clear it
  if (isAlias && bulkMode && formData['alias'] !== '') {
    setTimeout(() => {
      setFormData((prev) => ({ ...prev, alias: '' }));
    }, 0);
  }

  return (
    <div
      className={isAlias && bulkMode ? 'opacity-50' : ''}
      style={isAlias && bulkMode ? { cursor: 'not-allowed' } : undefined}
    >
      <div>{field.label}</div>
      <FusionInputField
        value={value}
        onChange={(val) => setFormData({ ...formData, [field['data-field']]: val })}
        disabled={isAlias && bulkMode}
        placeholder={isAlias && bulkMode && 'Aliases disabled in bulk mode'}
      />
    </div>
  );
};

/**
 * Render a valid-device dropdown populated from `deviceTypes`.
 *
 * When `device` is provided (editing an existing device) the control
 * is not rendered.
 *
 * @param {object} formData
 * @param {function} setFormData
 * @param {object} field
 * @param {Array<object>} deviceTypes
 * @param {object|null} device
 * @returns {JSX.Element|null}
 */
const renderValidDevice = (formData, setFormData, field, deviceTypes, device) => {
  if (device) return null;

  const systemType = formData.system_type;
  const deviceTypeName = formData.device_type;
  const deviceTypeEntry = deviceTypes
    ?.find((sys) => sys.system_type === systemType)
    ?.device_types?.find((t) => t.type === deviceTypeName);

  const bulkConfig = getBulkSelectConfig(systemType, deviceTypeName);
  const validDevices = deviceTypeEntry?.validDevices;

  const bulkToggle = bulkConfig ? (
    <Form.Check
      type="switch"
      id="bulk-add-toggle"
      label={`Add multiple ${bulkConfig.itemLabel}s at once`}
      checked={!!formData.bulk_mode}
      onChange={(e) =>
        setFormData({
          ...formData,
          bulk_mode: e.target.checked,
          // clear the other mode's selection state on toggle
          selected_devices: e.target.checked ? [] : undefined,
          [field['data-field']]: null,
          device: null,
        })
      }
    />
  ) : null;

  if (bulkConfig && formData.bulk_mode) {
    return (
      <>
        {bulkToggle}
        <BulkDeviceMultiSelect
          validDevices={validDevices || []}
          selectedDevices={formData.selected_devices || []}
          onChange={(devices) => setFormData({ ...formData, selected_devices: devices })}
          itemLabel={bulkConfig.itemLabel}
        />
      </>
    );
  }

  const validDeviceOptions = (validDevices || []).map((d) => ({
    value: d?.id,
    label: `${d?.node_id} ${d?.name}`,
    device: d,
  }));

  return (
    <>
      {bulkToggle}
      <BaseSelect
        label={field.label}
        isSearchable
        // Clearing is how the operator gets back to the neutral "Select" state after a
        // wrong pick, which the old dropdown's own "Select" row used to provide. It drops
        // the picked device the same way that row did — and the same way the bulk toggle
        // above already does.
        isClearable
        selectProps={{
          options: validDeviceOptions,
          value: findSelectOption(validDeviceOptions, formData[field['data-field']]),
          placeholder: 'Select',
          onChange: (option) =>
            setFormData({
              ...formData,
              // The device's own id, so a required-field check sees a truthy value.
              [field['data-field']]: option?.value ?? null,
              device: option?.device ?? null,
            }),
        }}
      />
    </>
  );
};
